import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { clearSessionData } from '../lib/supabase';

interface OtpContextType {
  generateOtp: (userId: string) => Promise<void>;
  validateOtp: (userId: string, otp: string) => Promise<boolean>;
  isLoading: boolean;
  hasInitialized: boolean;
  isEnabled: boolean;
  checkUserRole: () => Promise<boolean>;
}

const OtpContext = createContext<OtpContextType | undefined>(undefined);

interface OtpProviderProps {
  children: ReactNode;
}

// Cache para la sesión
let sessionCache: { session: any; timestamp: number } | null = null;
const SESSION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Función para verificar si OTP está habilitado
const isOtpEnabled = () => {
  return import.meta.env.VITE_ENABLE_OTP === 'true';
};

export function OtpProvider({ children }: OtpProviderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const hasInitialized = useRef(false);
  const isEnabled = isOtpEnabled();

  const getSession = async () => {
    const now = Date.now();
    if (sessionCache && (now - sessionCache.timestamp) < SESSION_CACHE_DURATION) {
      return sessionCache.session;
    }

    const { data: { session } } = await supabase().auth.getSession();
    sessionCache = { session, timestamp: now };
    return session;
  };

  const checkUserRole = async () => {
    try {
      const session = await getSession();
      if (!session?.user) return false;

      // Usar el perfil de Redux en lugar de hacer fetch
      const profile = session.user.user_metadata?.profile;
      return profile?.role === 'super_admin';
    } catch (error) {
      console.error('Error al verificar rol de usuario:', error);
      return false;
    }
  };

  const generateOtp = async (userId: string) => {
    if (!isEnabled) {
      console.log('OTP está deshabilitado, omitiendo generación');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Generando OTP para usuario:', userId);

      const response = await fetch('https://codytion.com/app-n8n/webhook/generate-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!response.ok) {
        throw new Error('Error al generar OTP');
      }

      hasInitialized.current = true;
      toast.success('Código OTP enviado a tu correo electrónico');
    } catch (error) {
      console.error('Error al generar OTP:', error);
      toast.error('Error al generar el código OTP');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const validateOtp = async (userId: string, otp: string): Promise<boolean> => {
    if (!isEnabled) {
      console.log('OTP está deshabilitado, omitiendo validación');
      return true;
    }

    try {
      setIsLoading(true);
      console.log('Validando OTP para usuario:', userId);

      const { error } = await supabase()
        .from('profiles')
        .update({ otp_validated: true })
        .eq('id', userId)
        .eq('otp', otp);

      if (error) {
        console.error('Error al validar OTP:', error);
        return false;
      }

      // Refrescar la sesión para actualizar el estado de autenticación
      await supabase().auth.refreshSession();
      
      hasInitialized.current = false;
      return true;
    } catch (error) {
      console.error('Error al validar OTP:', error);
      toast.error('Error al validar el código OTP');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Limpiar el estado de validación cuando se cierra sesión
  useEffect(() => {
    const { data: { subscription } } = supabase().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        try {
          // Limpiar localStorage
          void clearSessionData();
          
          // Limpiar estado
          hasInitialized.current = false;
          setIsLoading(false);
        } catch (error) {
          console.error('Error al limpiar localStorage:', error);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <OtpContext.Provider value={{ generateOtp, validateOtp, isLoading, hasInitialized: hasInitialized.current, isEnabled, checkUserRole }}>
      {children}
    </OtpContext.Provider>
  );
}

export function useOtp() {
  const context = useContext(OtpContext);
  if (context === undefined) {
    throw new Error('useOtp debe ser usado dentro de un OtpProvider');
  }
  return context;
} 